// @generated-imports
import { dom } from './core.js';
import { FOCUSABLE_SELECTOR } from './lite.js';
import { updatePadLegend } from './platform.js';
// @generated-imports
/* ===========================================================================
   GERENCIADOR DE CAMADAS (v2.0)
   ---------------------------------------------------------------------------
   Um único lugar decide qual painel está no topo, para onde vai o foco, o
   que "confirmar" (A / Enter) e "cancelar" (B / Esc) fazem e como o d-pad
   navega. Todos os painéis se registram aqui — nenhum `if (modalOpen)`
   avulso no resto do código. Camadas registradas: detalhes do jogo,
   backup, ajuda, configurações, seletor de core, confirmação, estados,
   retomar, jogadores, fila de importação e edição de ROM local.
   ======================================================================== */

const layerRegistry = new Map();   // id → definição
const layerStack = [];             // ids abertos, do fundo ao topo

/**
 * def = {
 *   id, element (overlay), card (elemento que recebe o trap de foco),
 *   close(): fecha (obrigatório),
 *   confirm(): ação do A/Enter (padrão: clica o elemento focado no card),
 *   move(direction): navegação do d-pad (padrão: cima/baixo circulam),
 *   legend: [[tecla, rótulo], ...] para a legenda de botões,
 *   focusOnOpen: elemento a focar ao abrir (opcional),
 * }
 */
function registerLayer(def) {
  if (!def || !def.id) return;
  layerRegistry.set(def.id, def);
}

function layerOpened(id, focusTarget) {
  const def = layerRegistry.get(id);
  if (!def) return;
  const index = layerStack.indexOf(id);
  if (index >= 0) layerStack.splice(index, 1);
  layerStack.push(id);
  def.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  document.body.classList.add('has-modal');
  const target = focusTarget || def.focusOnOpen || firstFocusableIn(def.card);
  if (target) { try { target.focus(); } catch (_) {} }
  if (typeof updatePadLegend === 'function') updatePadLegend();
}

function layerClosed(id) {
  const def = layerRegistry.get(id);
  const index = layerStack.indexOf(id);
  if (index >= 0) layerStack.splice(index, 1);
  if (!layerStack.length) document.body.classList.remove('has-modal');
  if (def && def.lastFocus && document.contains(def.lastFocus)) {
    try { def.lastFocus.focus(); } catch (_) {}
  }
  if (def) def.lastFocus = null;
  if (typeof updatePadLegend === 'function') updatePadLegend();
}

function topLayer() {
  // Ignora camadas cujo elemento foi escondido por fora do gerenciador.
  for (let i = layerStack.length - 1; i >= 0; i -= 1) {
    const def = layerRegistry.get(layerStack[i]);
    if (def && def.element && !def.element.classList.contains('is-hidden')) return def;
    layerStack.splice(i, 1);
  }
  if (!layerStack.length) document.body.classList.remove('has-modal');
  return null;
}

function isLayerOpen(id) {
  // Aberta = aberta pelo gerenciador (na pilha) E visível. Só a visibilidade
  // não basta: um elemento sem a classe inicial contaria como aberto.
  const def = layerRegistry.get(id);
  return Boolean(def && def.element && layerStack.includes(id) && !def.element.classList.contains('is-hidden'));
}

function anyLayerOpen() {
  return topLayer() !== null;
}

function closeTopLayer() {
  const def = topLayer();
  if (!def) return false;
  def.close();
  return true;
}

function layerConfirm() {
  const def = topLayer();
  if (!def) return false;
  if (typeof def.confirm === 'function') { def.confirm(); return true; }
  const active = document.activeElement;
  if (active && def.card && def.card.contains(active) && typeof active.click === 'function') active.click();
  return true;
}

function layerMove(direction) {
  const def = topLayer();
  if (!def) return false;
  if (typeof def.move === 'function') { def.move(direction); return true; }
  cycleFocusIn(def.card, direction);
  return true;
}

function focusablesIn(container) {
  if (!container) return [];
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)]
    .filter((el) => !el.disabled && el.offsetParent !== null && el.getAttribute('aria-hidden') !== 'true');
}

function firstFocusableIn(container) {
  return focusablesIn(container)[0] || null;
}

/** Cima/baixo (e esquerda/direita, como fallback) circulam os focáveis. */
function cycleFocusIn(container, direction) {
  const items = focusablesIn(container);
  if (!items.length) return;
  const step = (direction === 'down' || direction === 'right') ? 1 : -1;
  let index = items.indexOf(document.activeElement);
  index = index < 0 ? 0 : (index + step + items.length) % items.length;
  try { items[index].focus(); } catch (_) {}
}

/** Trap de Tab: o foco não escapa da camada do topo. */
function trapFocusInTopLayer(event) {
  const def = topLayer();
  if (!def || !def.card) return;
  const items = focusablesIn(def.card);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (!def.card.contains(active)) { event.preventDefault(); first.focus(); return; }
  if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
}

/* --- Camada de confirmação genérica (importar, compatibilidade, remoções) --- */

function openConfirmLayer(spec) {
  if (!dom.confirmLayer) return;
  dom.confirmTitle.textContent = spec.title || 'Confirmar';
  dom.confirmMessage.textContent = spec.message || '';
  dom.confirmActions.innerHTML = '';
  const actions = (spec.actions || []).concat([{ label: spec.cancelLabel || 'Cancelar', kind: 'cancel' }]);
  actions.forEach((action, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = action.kind === 'danger' ? 'btn btn-soft confirm-danger'
      : action.kind === 'cancel' ? 'btn btn-ghost' : 'btn btn-primary';
    button.textContent = action.label;
    button.addEventListener('click', () => {
      closeConfirmLayer();
      if (typeof action.handler === 'function') action.handler();
    });
    if (index === 0) button.dataset.primary = '1';
    dom.confirmActions.appendChild(button);
  });
  dom.confirmLayer.classList.remove('is-hidden');
  layerOpened('confirm', dom.confirmActions.querySelector('[data-primary]'));
}

function closeConfirmLayer() {
  if (!dom.confirmLayer) return;
  dom.confirmLayer.classList.add('is-hidden');
  layerClosed('confirm');
}


/* --- Camada de texto (prompt) --------------------------------------------- */

let promptResolve = null;

/** Pede um texto ao usuário. Resolve com o texto (aparado) ou null. */
function openPromptLayer(spec = {}) {
  return new Promise((resolve) => {
    if (!dom.promptLayer) { resolve(null); return; }
    if (promptResolve) promptResolve(null);
    promptResolve = resolve;
    dom.promptTitle.textContent = spec.title || 'Digite';
    dom.promptLabel.textContent = spec.label || '';
    dom.promptInput.value = spec.value || '';
    dom.promptInput.placeholder = spec.placeholder || '';
    dom.promptLayer.classList.remove('is-hidden');
    layerOpened('prompt', dom.promptInput);
    try { dom.promptInput.select(); } catch (_) {}
  });
}

function finishPrompt(value) {
  const resolve = promptResolve;
  promptResolve = null;
  if (dom.promptLayer) dom.promptLayer.classList.add('is-hidden');
  layerClosed('prompt');
  if (resolve) resolve(value);
}

function submitPrompt() {
  const text = String(dom.promptInput.value || '').trim();
  finishPrompt(text || null);
}

function cancelPrompt() {
  finishPrompt(null);
}

// @generated-exports
export { anyLayerOpen, cancelPrompt, closeConfirmLayer, closeTopLayer, cycleFocusIn, finishPrompt, firstFocusableIn, focusablesIn, isLayerOpen, layerClosed, layerConfirm, layerMove, layerOpened, layerRegistry, layerStack, openConfirmLayer, openPromptLayer, promptResolve, registerLayer, submitPrompt, topLayer, trapFocusInTopLayer };
// @generated-exports
